// src/App.js
import React, { useEffect, useState, useRef } from 'react';
import '../CSS/App.css';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus, EventType } from '@azure/msal-browser';
import msalConfig from '../auth/msalConfig.js';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate, useNavigate } from 'react-router-dom';

import Navbar from './Navbar.js';
import Footer from '../Components/Footer.js';
import ScrollToTop from '../Components/ScrollToTop.js';
import Login from '../Pages/Login.js';
import SetPassword from '../Pages/SetPassword.js';
import Home from '../Pages/Home.js';
import Reports from '../Pages/Reports.js';
import Countries from '../Pages/Regions.js';
import Orgs from '../Pages/Orgs.js';
import OrgDetails from '../Pages/OrgDetails.js';
import RegionDetails from '../Pages/Regionsdetails.js';
import Privacy from '../Pages/Privacy.js';
import Highlights from '../Pages/Highlights.js';
import PowerBI from '../Pages/PowerBi.js';
import Umbrella from '../Pages/Umbrella.js';
import Calendar from '../Pages/Calendar.js';
import ContactUs from '../Pages/ContactUs.js';
import Financials from '../Pages/Financial.js';
import AALAC from '../Pages/AALAC.js';
import WeeklyIntsumAR from '../Pages/WeeklyIntsumAR.js';
import WeeklyIntsumES from '../Pages/WeeklyIntsumES.js';
import Spotlights from '../Pages/Spotlights.js';
import ThreatAssessment from '../Pages/ThreatAssessmentSnapshot.js';
import AnnualAssessment from '../Pages/AnnualAssessment.js';
import Complaints from '../Pages/Complaints.js';
import SecurityPlanning from '../Pages/SecurityPlanning.js';
import USDA from '../Pages/USDA.js';
import DM from '../Pages/DM.js';
import Prio3 from '../Pages/Prio3Search.js';
import LegacySearch from '../Pages/LegacySearch.js';
import NonProfit from '../Pages/501c3.js';
import ARHub from '../Pages/ARHub.js';
import RouteTracker from '../Components/RouteTracker.js';

const msalInstance = new PublicClientApplication(msalConfig);

/* --------------------- CUSTOM-AUTH GUARD HELPERS --------------------- */

/** Force logout for *custom* auth only (does NOT touch MSAL) */
async function forceCustomLogout() {
  try {
    await fetch('/api/logout', { method: 'POST', credentials: 'include' }); // clear HttpOnly cookie/session server-side
  } catch (_) {}
  localStorage.removeItem('custom-auth');
  localStorage.removeItem('custom-email');
  localStorage.removeItem('user-email');
  window.location.href = '/login';
}

/**
 * Custom-auth cookie guard
 * - runs only when `enabled` is true
 * - skips auth pages (/login, /set-password)
 * - pings /api/custom-auth-check; if 401/403 -> forces custom logout
 */
function useCustomCookieGuard({
  enabled,
  skipPaths = ['/login', '/set-password'],
  runOnRouteChange = true,
} = {}) {
  const location = useLocation();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (skipPaths.includes(location.pathname)) return;

    // only once on mount if runOnRouteChange=false
    if (ranRef.current && !runOnRouteChange) return;
    ranRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/custom-auth-check', {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok && !cancelled) {
          await forceCustomLogout();
        }
      } catch {
        if (!cancelled) {
          await forceCustomLogout();
        }
      }
    })();

    return () => { cancelled = true; };
  }, [enabled, location.pathname, runOnRouteChange, skipPaths]);
}

/* -------------------------------------------------------------------- */

function AppRoutes() {
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();

  const customAuth = localStorage.getItem('custom-auth') === 'true';
  const unifiedEmail = localStorage.getItem('user-email');
  const msalAuthenticated = accounts.length > 0;
  const isAuthenticated = msalAuthenticated || customAuth;

  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const path = location.pathname;
  const isAuthPage = path === '/login' || path === '/set-password';

  // Ensure MSAL sets an active account on login success (helps restore on refresh)
  useEffect(() => {
    const cbId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
        instance.setActiveAccount(event.payload.account);
      }
    });
    return () => { if (cbId) instance.removeEventCallback(cbId); };
  }, [instance]);

  // 🔒 Silent cookie auto-heal (MSAL only): keep as-is
  const cookieSyncing = useRef(false);
  useEffect(() => {
    if (cookieSyncing.current) return;
    if (inProgress !== InteractionStatus.None) return; // wait until MSAL is idle
    cookieSyncing.current = true;

    let cancelled = false;

    (async () => {
      try {
        // Check if the cookie is already valid
        const meRes = await fetch('/api/me', { credentials: 'include' });
        const me = await meRes.json().catch(() => ({ authenticated: false }));
        if (cancelled) return;

        if (me.authenticated) return; // cookie is fine

        // If MSAL has a user, silently create the cookie
        const acct = instance.getActiveAccount() || accounts[0];
        const email =
          acct?.username ||
          acct?.idTokenClaims?.preferred_username ||
          acct?.idTokenClaims?.email;

        if (email) {
          await fetch('/api/msal-login', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          }).catch(() => {});
          // Optional: reload to ensure any immediately-following fetches include the cookie
          // window.location.reload();
        }
      } finally {
        // Keep latched to avoid hammering; remove next line if you want to re-check every route change
        // cookieSyncing.current = false;
      }
    })();

    return () => { cancelled = true; };
  }, [location.pathname, inProgress, accounts, instance]);

  /* ✅ Only for CUSTOM users — validate server cookie and force custom logout if invalid
       (skip on /login and /set-password) */
  useCustomCookieGuard({
    enabled: customAuth && isAuthenticated && !isAuthPage,
    runOnRouteChange: true,
  });

  // Fetch status for custom login; MSAL users are always 'Active' for routing purposes
  useEffect(() => {
    const fetchStatus = async () => {
      if (customAuth && unifiedEmail) {
        try {
          const res = await fetch(`/api/get-status?email=${encodeURIComponent(unifiedEmail)}`);
        const json = await res.json();
          setUserStatus(json.status); // 'New', 'Active', or 'Locked'
        } catch (err) {
          console.error('Error fetching user status:', err);
          setUserStatus('Unknown');
        }
      } else if (msalAuthenticated) {
        setUserStatus('Active');
      } else {
        setUserStatus(null);
      }
      setLoading(false);
    };

    fetchStatus();
  }, [customAuth, unifiedEmail, msalAuthenticated]);

  if (loading) return null;

  // Redirect logic
  if (!isAuthenticated && path !== '/login') {
    return <Navigate to="/login" replace />;
  }

  if (userStatus === 'New' && path !== '/set-password') {
    return <Navigate to="/set-password" replace />;
  }

  if (userStatus === 'Locked') {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="App">
      {!isAuthPage && isAuthenticated && <Navbar />}
      <ScrollToTop />
      <header className="App-header">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/set-password" element={<SetPassword />} />
          {isAuthenticated && (
            <>
              <Route index element={<Home />} />
              <Route path="/home" element={<Home />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/region" element={<Countries />} />
              <Route path="/orgs" element={<Orgs />} />
              <Route path="/orgdetails/:id" element={<OrgDetails />} />
              <Route path="/regions/:region" element={<RegionDetails />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/highlights" element={<Highlights />} />
              <Route path="/powerbi" element={<PowerBI />} />
              <Route path="/:companyName" element={<Umbrella />} />
              <Route path="/upcoming" element={<Calendar />} />
              <Route path="/contactus" element={<ContactUs />} />
              <Route path="/spotlights" element={<Spotlights />} />
              <Route path="/threatassessmentsnapshot" element={<ThreatAssessment />} />
              <Route path="/annualassessment" element={<AnnualAssessment />} />
              <Route path="/complaints" element={<Complaints />} />
              <Route path="/securityplanning" element={<SecurityPlanning />} />
              <Route path="/financials" element={<Financials />} />
              <Route path="/aaalac" element={<AALAC />} />
              <Route path="/usda" element={<USDA />} />
              <Route path="/dmreports" element={<DM />} />
              <Route path="/prio3" element={<Prio3 />} />
              <Route path="/legacysearch" element={<LegacySearch />} />
              <Route path="/weeklyintsumar" element={<WeeklyIntsumAR />} />
              <Route path="/weeklyintsumes" element={<WeeklyIntsumES />} />
              <Route path="/nonprofit" element={<NonProfit />} />
              <Route path="/dailies" element={<DM />} />
              <Route path="/arhub" element={<ARHub />} />
            </>
          )}
        </Routes>
      </header>
      {!isAuthPage && isAuthenticated && <Footer />}
    </div>
  );
}

function AuthenticatedApp() {
  return (
    <MsalProvider instance={msalInstance}>
      <Router>
      <RouteTracker />
        <AppRoutes />
      </Router>
    </MsalProvider>
  );
}

export default AuthenticatedApp;
